import { Fragment, useState } from "react";
import { format, isSameMonth, isToday, parseISO } from "date-fns";
import { nn } from "date-fns/locale";
import { Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toISODate, WEEKDAYS_SHORT, capitalize } from "@/lib/format";
import { cn } from "@/lib/utils";
import { CalBar, CalDot } from "./Bar";
import { isoWeek, onDay, sortByStart, weekSegments, weekdayIndex, weeksOfMonth } from "./engine";

const MAX_LANES = 3;

/** Everything on one day – used by the «+N meir» popover and the mobile agenda. */
const DayList = ({ items, colours, onOpen, onCreate, iso, className }) => (
  <div className={className}>
    {items.length === 0 ? <p className="px-1 py-2 text-sm text-muted-foreground">Ingenting denne dagen.</p> : (
      <ul className="space-y-1">{items.map((it) => <li key={it.key}><CalBar item={it} colours={colours} onOpen={onOpen} /></li>)}</ul>
    )}
    {onCreate && <button type="button" onClick={() => onCreate(iso)} className="mt-2 inline-flex h-9 items-center gap-1.5 rounded-md px-2 text-sm font-medium text-foreground hover:bg-secondary" data-testid={`cal-day-create-${iso}`}><Plus className="h-4 w-4" /> Ny oppføring {format(parseISO(iso), "d. MMM", { locale: nn })}</button>}
  </div>
);

const DayCell = ({ day, month, iso, items, colours, compact, selected, onSelect, onCreate, onMove, hidden, onOpen }) => {
  const [over, setOver] = useState(false);
  const today = isToday(day);
  const outside = !isSameMonth(day, month);
  const wd = weekdayIndex(day);
  const canDrop = (e) => e.dataTransfer?.types?.includes("text/plain");
  return (
    <div
      role={compact ? "button" : undefined}
      tabIndex={compact ? 0 : undefined}
      onClick={() => (compact ? onSelect(iso) : onCreate(iso))}
      onKeyDown={compact ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(iso); } } : undefined}
      onDragOver={(e) => { if (canDrop(e)) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (!over) setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const id = e.dataTransfer.getData("text/plain"); if (id) onMove({ id, start_date: iso }); }}
      className={cn(
        "relative min-w-0 border-l border-border transition-colors first:border-l-0",
        compact ? "flex min-h-[64px] flex-col items-center pt-2" : "min-h-[132px] cursor-cell hover:bg-secondary/40",
        wd >= 5 && "cal-weekend",
        today && "cal-today",
        outside && "cal-outside",
        over && "cal-drop",
        compact && selected && "bg-secondary/70",
      )}
      data-testid={`cal-day-${iso}`}
      aria-label={format(day, "EEEE d. MMMM", { locale: nn })}
    >
      <span className={cn("cal-daynum flex h-7 w-7 items-center justify-center rounded-full text-[13px] tabular", compact ? "" : "absolute left-1.5 top-1.5", today ? "bg-sol font-semibold text-ink" : "font-medium")} data-testid={today ? "cal-today-marker" : undefined}>{format(day, "d")}</span>
      {compact && items.length > 0 && (
        <span className="mt-1.5 flex max-w-full flex-wrap justify-center gap-[3px] px-1" aria-label={`${items.length} oppføringar`}>{items.slice(0, 5).map((it) => <CalDot key={it.key} item={it} colours={colours} />)}</span>
      )}
      {!compact && hidden > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" onClick={(e) => e.stopPropagation()} className="absolute bottom-1 left-1.5 inline-flex h-6 items-center rounded-md px-1.5 text-[12px] font-medium text-muted-foreground hover:bg-secondary hover:text-foreground" data-testid={`cal-more-${iso}`}>+{hidden} meir</button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 bg-popover p-3" onClick={(e) => e.stopPropagation()}>
            <p className="t-mono mb-2 text-muted-foreground">{capitalize(format(day, "EEEE d. MMMM", { locale: nn }))}</p>
            <DayList items={items} colours={colours} onOpen={onOpen} onCreate={onCreate} iso={iso} />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

/**
 * Month grid: Monday first, ISO week numbers, multi-day bars with lane packing, drag-and-drop between days.
 * Under `md` it collapses to dots + an agenda for the tapped day.
 */
export const MonthView = ({ month, items, colours, compact = false, selectedDay, onSelectDay, onOpen, onCreate, onMove, onGoWeek }) => {
  const weeks = weeksOfMonth(month);
  const selectedISO = selectedDay || toISODate(new Date());
  const selectedItems = sortByStart(items.filter((it) => onDay(it, selectedISO)));
  return (
    <div data-testid="cal-month-view">
      <div className="overflow-hidden admin-card">
        <div className={cn("grid border-b border-border text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground", compact ? "grid-cols-7" : "grid-cols-[40px_repeat(7,minmax(0,1fr))]")}>
          {!compact && <div className="px-1 py-2" aria-hidden="true" />}
          {WEEKDAYS_SHORT.map((d, i) => <div key={d} className={cn("px-2 py-2", compact && "text-center", i >= 5 && "text-muted-foreground/70")}>{compact ? d.slice(0, 2) : d}</div>)}
        </div>
        {weeks.map((days) => {
          const segs = compact ? [] : weekSegments(items, days);
          const hiddenPerDay = Array(7).fill(0);
          if (!compact) for (const s of segs) if (s.lane >= MAX_LANES) for (let d = s.start; d <= s.end; d += 1) hiddenPerDay[d] += 1;
          const wk = isoWeek(days[0]);
          return (
            <div key={toISODate(days[0])} className={cn("relative grid border-b border-border last:border-b-0", compact ? "grid-cols-7" : "grid-cols-[40px_repeat(7,minmax(0,1fr))]")} data-testid={`cal-week-row-${wk}`}>
              {!compact && (
                <button type="button" onClick={() => onGoWeek?.(days[0])} className="flex flex-col items-center justify-start gap-0.5 border-r border-border bg-secondary/40 pt-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground" aria-label={`Gå til veke ${wk}`} data-testid={`cal-weeknum-${wk}`}>
                  <span className="t-mono-sm">V</span>
                  <span className="text-[13px] font-semibold tabular">{wk}</span>
                </button>
              )}
              {days.map((day) => {
                const iso = toISODate(day);
                const di = weekdayIndex(day);
                return (
                  <DayCell key={iso} day={day} iso={iso} month={month} items={compact ? sortByStart(items.filter((it) => onDay(it, iso))) : sortByStart(items.filter((it) => onDay(it, iso)))} colours={colours} compact={compact} selected={selectedISO === iso} onSelect={onSelectDay} onCreate={onCreate} onMove={onMove} hidden={hiddenPerDay[di]} onOpen={onOpen} />
                );
              })}
              {!compact && (
                <div className="pointer-events-none absolute inset-y-0 left-[40px] right-0 grid grid-cols-7 content-start gap-y-[3px] pt-[38px]" style={{ gridAutoRows: "26px" }}>
                  {segs.filter((s) => s.lane < MAX_LANES).map((s) => (
                    <div key={s.item.key} className="pointer-events-auto min-w-0 px-[3px]" style={{ gridColumn: `${s.start + 1} / ${s.end + 2}`, gridRow: s.lane + 1 }}>
                      <CalBar item={s.item} colours={colours} before={s.before} after={s.after} onOpen={onOpen} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {compact && (
        <div className="mt-4 admin-card p-3" data-testid="cal-mobile-agenda">
          <p className="t-mono mb-2 text-muted-foreground">{capitalize(format(parseISO(selectedISO), "EEEE d. MMMM", { locale: nn }))}</p>
          <DayList items={selectedItems} colours={colours} onOpen={onOpen} onCreate={onCreate} iso={selectedISO} />
        </div>
      )}
    </div>
  );
};

export const MonthSkeleton = () => (
  <div className="overflow-hidden admin-card" aria-busy="true">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="grid grid-cols-7 border-b border-border last:border-b-0">
        {Array.from({ length: 7 }).map((__, j) => (
          <Fragment key={j}><div className="min-h-[132px] border-l border-border p-2 first:border-l-0"><div className="h-6 w-6 animate-pulse rounded-full bg-secondary" />{(i + j) % 3 === 0 && <div className="mt-4 h-5 animate-pulse rounded bg-secondary" />}</div></Fragment>
        ))}
      </div>
    ))}
  </div>
);
